import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import { useSupabaseStorage } from '../../../hooks/useSupabaseStorage';
import { supabase } from '../../../lib/supabase';

interface UploadedFile {
  url: string;
  path: string;
  fileName: string;
  type: 'image' | 'video';
}

const LerockPage = () => {
  const { t, i18n } = useTranslation();
  const [expandedBox, setExpandedBox] = useState<number | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: number]: UploadedFile }>({});
  const [captions, setCaptions] = useState<{ [key: number]: string }>({});
  const [uploadingBoxes, setUploadingBoxes] = useState<{ [key: number]: boolean }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingCaptions, setSavingCaptions] = useState<{ [key: number]: boolean }>({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const captionTimeouts = useRef<{ [key: number]: ReturnType<typeof setTimeout> }>({});
  const { uploadFile, deleteFile, uploading, uploadProgress } = useSupabaseStorage();

  const projectLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Wild Cherry', href: '/project/wildcherry' },
    { name: "Le Veau d'Or", href: '/project/leveaudor' },
    { name: 'Le Rock', href: '/project/lerock' },
    { name: 'Frenchette', href: '/project/frenchette' }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": "Le Rock - Creative Design Project",
    "description": "Le Rock project by Dava Nasr - Creative graphic design and brand development",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/project/lerock`,
    "creator": {
      "@type": "Person",
      "name": "Dava Nasr",
      "jobTitle": "Graphic Designer"
    },
    "image": "https://static.readdy.ai/image/b035185ce40665b0d42a9231037815e9/85cbb154075f8e015922171d3404e05f.jpeg"
  };

  // Load existing files from database on component mount and language change
  useEffect(() => {
    loadExistingFiles();
  }, []);

  const handleMobileNavClick = (href: string) => {
    setIsMenuOpen(false);
    // Scroll to top on mobile when navigating
    if (window.innerWidth < 768) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  // Reload captions when language changes
  useEffect(() => {
    loadExistingFiles();
  }, [i18n.language]);

  const loadExistingFiles = async () => {
    try {
      setLoading(true);
      const currentLanguage = i18n.language;
      const tableName = currentLanguage === 'fr' ? 'lerock_uploads_fr' : 'lerock_uploads';

      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .order('box_index');

      if (error) {
        console.error('Error loading files:', error);
        setError(`Failed to load files: ${error.message}`);
        return;
      }

      const filesMap: { [key: number]: UploadedFile } = {};
      const captionsMap: { [key: number]: string } = {};

      data?.forEach(file => {
        filesMap[file.box_index] = {
          url: file.file_url,
          path: file.file_path,
          fileName: file.file_name,
          type: file.file_type as 'image' | 'video',
        };
        if (file.caption) {
          captionsMap[file.box_index] = file.caption;
        }
      });

      // Set default caption for tile 5 based on language
      if (currentLanguage === 'fr') {
        captionsMap[5] = t('leRockCaption');
      } else if (!captionsMap[5]) {
        captionsMap[5] = 'New Year\'s Eve artwork for LE ROCK.';
      }

      setUploadedFiles(filesMap);
      setCaptions(captionsMap);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load files from database');
    } finally {
      setLoading(false);
    }
  };

  const saveFileToDatabase = async (index: number, fileData: UploadedFile, caption?: string) => {
    try {
      const currentLanguage = i18n.language;
      const tableName = currentLanguage === 'fr' ? 'lerock_uploads_fr' : 'lerock_uploads';

      const { data, error } = await supabase
        .from(tableName)
        .upsert({
          box_index: index,
          file_name: fileData.fileName,
          file_path: fileData.path,
          file_url: fileData.url,
          file_type: fileData.type,
          caption: caption || '',
          uploaded_at: new Date().toISOString(),
        })
        .select();

      if (error) {
        console.error('Database save error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      return data;
    } catch (err) {
      console.error('Database save error:', err);
      throw err;
    }
  };

  const saveCaptionToDatabase = async (boxIndex: number, caption: string) => {
    try {
      setSavingCaptions(prev => ({ ...prev, [boxIndex]: true }));
      const currentLanguage = i18n.language;
      const tableName = currentLanguage === 'fr' ? 'lerock_uploads_fr' : 'lerock_uploads';

      const { data: existingData, error: selectError } = await supabase
        .from(tableName)
        .select('*')
        .eq('box_index', boxIndex)
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        throw selectError;
      }

      if (existingData) {
        const { error } = await supabase
          .from(tableName)
          .update({ caption })
          .eq('box_index', boxIndex);
        if (error) {
          throw new Error(`Caption update error: ${error.message}`);
        }
      } else {
        const { error } = await supabase
          .from(tableName)
          .insert({
            box_index: boxIndex,
            caption,
            file_name: '',
            file_path: '',
            file_url: '',
            file_type: 'image',
            uploaded_at: new Date().toISOString(),
          });
        if (error) {
          throw new Error(`Caption insert error: ${error.message}`);
        }
      }

      console.log(`Caption saved for box ${boxIndex}:`, caption);
    } catch (err) {
      console.error('Caption save error:', err);
      throw err;
    } finally {
      setSavingCaptions(prev => ({ ...prev, [boxIndex]: false }));
    }
  };

  const removeFileFromDatabase = async (index: number) => {
    try {
      const currentLanguage = i18n.language;
      const tableName = currentLanguage === 'fr' ? 'lerock_uploads_fr' : 'lerock_uploads';

      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('box_index', index);

      if (error) {
        console.error('Database delete error:', error);
        throw new Error(`Database delete error: ${error.message}`);
      }
    } catch (err) {
      console.error('Database delete error:', err);
      throw err;
    }
  };

  const handleBoxClick = (index: number) => {
    // Only allow expand on desktop (md and above)
    if (window.innerWidth >= 768) {
      setExpandedBox(index);
    }
  };

  const handleFileUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingBoxes(prev => ({ ...prev, [index]: true }));
      setError(null);

      const result = await uploadFile(file);
      const fileType = index === 8 ? 'video' : 'image';

      const fileData: UploadedFile = {
        url: result.url,
        path: result.path,
        fileName: result.fileName,
        type: fileType,
      };

      await saveFileToDatabase(index, fileData, captions[index] || '');

      setUploadedFiles(prev => ({
        ...prev,
        [index]: fileData,
      }));

      setExpandedBox(null);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploadingBoxes(prev => ({ ...prev, [index]: false }));
      if (fileInputRefs.current[index]) {
        fileInputRefs.current[index]!.value = '';
      }
    }
  };

  const handleRemoveFile = async (index: number, event: React.MouseEvent) => {
    event.stopPropagation();

    const file = uploadedFiles[index];
    if (!file) return;

    try {
      setError(null);
      await deleteFile(file.path);
      await removeFileFromDatabase(index);

      setUploadedFiles(prev => {
        const copy = { ...prev };
        delete copy[index];
        return copy;
      });

      setCaptions(prev => {
        const copy = { ...prev };
        delete copy[index];
        return copy;
      });

      setExpandedBox(null);
    } catch (err) {
      console.error('Delete failed:', err);
      setError(`Delete failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleCaptionChange = (index: number, value: string) => {
    setCaptions(prev => ({ ...prev, [index]: value }));

    if (captionTimeouts.current[index]) {
      clearTimeout(captionTimeouts.current[index]);
    }

    captionTimeouts.current[index] = setTimeout(async () => {
      try {
        await saveCaptionToDatabase(index, value);
        console.log(`Auto-saved caption for box ${index}`);
      } catch (err) {
        console.error('Failed to auto-save caption:', err);
        setError(`Failed to save caption: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }, 1000);
  };

  // Zoom handlers
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setZoomPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - zoomPosition.x, y: e.clientY - zoomPosition.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setZoomPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(captionTimeouts.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-300 mb-4">
              <i className="ri-loader-4-line text-6xl animate-spin w-16 h-16 flex items-center justify-center mx-auto"></i>
            </div>
            <p className="text-gray-300 text-xl">Loading your files...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <SEOHead
        title="Le Rock - Dava Nasr Portfolio"
        description="Le Rock creative design project by Dava Nasr. Graphic design and brand development work."
        keywords="Le Rock, graphic design, brand development, creative design, Dava Nasr"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/project/lerock`}
        schemaData={schemaData}
      />
      
      <Header />

      {/* Hamburger Menu */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="bg-black border border-white/20 shadow-lg rounded-lg p-2 md:p-6 hover:bg-gray-900 transition-colors duration-200 cursor-pointer"
        >
          <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 md:w-10 md:h-10 flex items-center justify-center text-white`} style={{ strokeWidth: '3px', fontWeight: 'bold' }}></i>
        </button>

        {/* Mobile Dropdown Menu */}
        {isMenuOpen && (
          <div className="absolute top-full right-0 mt-1 bg-black border border-white/20 shadow-lg rounded-lg py-0.5 md:py-4 min-w-[100px] md:min-w-[200px]">
            {projectLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => handleMobileNavClick(link.href)}
                className="block px-3 py-1 md:px-6 md:py-3 text-xs md:text-xl font-medium text-white hover:bg-gray-900 hover:text-teal-400 transition-colors duration-200 cursor-pointer whitespace-nowrap text-right"
              >
                {link.name}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 mx-8 mt-4 rounded">
          <div className="flex items-center">
            <i className="ri-error-warning-line mr-2 w-5 h-5 flex items-center justify-center"></i>
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-300 hover:text-red-100">
              <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col items-center pt-16 md:pt-32 pb-16">
        <h1 className="text-4xl md:text-8xl font-bold text-white mb-8 md:mb-16">le rock</h1>

        {/* 3x3 Grid of Expandable Boxes - Now showing tiles 0-8 (9 total) */}
        <div className="w-full max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 9 }, (_, index) => (
              <div key={index} className="space-y-4">
                <div className="relative">
                  <input
                    ref={el => (fileInputRefs.current[index] = el)}
                    type="file"
                    accept={
                      index === 8
                        ? 'video/*'
                        : index === 5 || index === 6
                        ? 'image/jpeg,image/jpg'
                        : 'image/jpeg,image/jpg,image/png'
                    }
                    onChange={e => handleFileUpload(index, e)}
                    className="hidden"
                  />
                  <div
                    className={`
                      border-2 border-gray-600 bg-gray-800 cursor-pointer
                      transition-all duration-300 hover:border-gray-500 h-auto md:h-60 lg:h-80 relative overflow-hidden
                      flex items-center justify-center
                      aspect-auto md:aspect-square
                    `}
                    onClick={() => handleBoxClick(index)}
                  >
                    {uploadingBoxes[index] ? (
                      <div className="text-center">
                        <div className="text-gray-300 mb-4">
                          <i className="ri-upload-cloud-line text-6xl animate-pulse w-16 h-16 flex items-center justify-center mx-auto"></i>
                        </div>
                        <p className="text-gray-300 text-lg">Uploading...</p>
                        <div className="w-32 bg-gray-600 rounded-full h-2 mt-4">
                          <div
                            className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    ) : uploadedFiles[index] ? (
                      <div className="relative w-full h-full">
                        {uploadedFiles[index].type === 'video' ? (
                          <>
                            {/* Mobile Video Display */}
                            <div className="block md:hidden">
                              {uploadedFiles[index].thumbnailUrl ? (
                                <div 
                                  className="relative w-full h-full cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const videoElement = document.createElement('video');
                                    videoElement.src = uploadedFiles[index].url;
                                    videoElement.controls = true;
                                    videoElement.autoplay = true;
                                    videoElement.playsInline = true;
                                    videoElement.className = 'w-full h-full object-contain';
                                    const container = e.currentTarget.parentElement;
                                    if (container) {
                                      container.innerHTML = '';
                                      container.appendChild(videoElement);
                                    }
                                  }}
                                >
                                  <img
                                    src={uploadedFiles[index].thumbnailUrl}
                                    alt="Video thumbnail"
                                    className="w-full h-auto md:w-full md:h-full object-contain"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="bg-black bg-opacity-60 rounded-full p-3 md:p-4">
                                      <i className="ri-play-fill text-white text-2xl md:text-4xl w-6 h-6 md:w-8 md:h-8 flex items-center justify-center"></i>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <video 
                                  src={uploadedFiles[index].url} 
                                  className="w-full h-full object-contain" 
                                  controls 
                                  playsInline
                                />
                              )}
                            </div>
                            
                            {/* Desktop Video Display */}
                            <div className="hidden md:block relative w-full h-full">
                              <video src={uploadedFiles[index].url} className="w-full h-full object-cover" />
                              {/* Video Play Button Overlay */}
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="bg-black bg-opacity-60 rounded-full p-3 md:p-4">
                                  <i className="ri-play-fill text-white text-2xl md:text-4xl w-6 h-6 md:w-8 md:h-8 flex items-center justify-center"></i>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <img
                            src={uploadedFiles[index].url}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-auto md:w-full md:h-full md:object-cover md:object-top"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-gray-400 mb-4">
                            <i className={`${index === 8 ? 'ri-video-line' : 'ri-image-line'} text-6xl w-16 h-16 flex items-center justify-center mx-auto`}></i>
                          </div>
                          <p className="text-gray-300 text-lg">
                            {index === 8
                              ? 'Upload Video'
                              : index === 5 || index === 6
                              ? 'Upload JPEG'
                              : `Upload Image ${index + 1}`}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Text Input Box - Clean and Borderless */}
                <div className="w-full">
                  <textarea
                    placeholder={
                      index === 5 
                        ? (i18n.language === 'fr' ? t('leRockCaption') : 'New Year\'s Eve artwork for LE ROCK.')
                        : "Enter your text here..."
                    }
                    className="w-full h-32 text-base text-white placeholder-gray-400 border-0 rounded-lg p-4 outline-none resize-none leading-relaxed transition-all duration-200 bg-gray-800 shadow-none"
                    value={
                      index === 5 
                        ? (i18n.language === 'fr' ? t('leRockCaption') : (captions[index] || 'New Year\'s Eve artwork for LE ROCK.'))
                        : (captions[index] || '')
                    }
                    onChange={e => handleCaptionChange(index, e.target.value)}
                    maxLength={1000}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Expanded Overlay - Desktop Only */}
      {expandedBox !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 items-center justify-center hidden md:flex"
          onClick={() => {
            setExpandedBox(null);
            resetZoom();
          }}
        >
          <div className="relative w-full h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => {
                setExpandedBox(null);
                resetZoom();
              }}
              className="absolute top-4 left-4 text-white hover:text-gray-300 text-lg z-20 bg-black/50 rounded-full p-2"
            >
              <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
            </button>

            {/* Zoom controls for images (panels 0-6) - Desktop only */}
            {uploadedFiles[expandedBox] && uploadedFiles[expandedBox].type === 'image' && expandedBox <= 6 && (
              <div className="absolute top-4 right-4 hidden md:flex flex-col gap-2 z-20">
                <button
                  onClick={handleZoomIn}
                  className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all"
                  disabled={zoomLevel >= 3}
                >
                  <i className="ri-zoom-in-line w-5 h-5 flex items-center justify-center"></i>
                </button>
                <button
                  onClick={handleZoomOut}
                  className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all"
                  disabled={zoomLevel <= 0.5}
                >
                  <i className="ri-zoom-out-line w-5 h-5 flex items-center justify-center"></i>
                </button>
                <button
                  onClick={resetZoom}
                  className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all"
                >
                  <i className="ri-fullscreen-exit-line w-5 h-5 flex items-center justify-center"></i>
                </button>
                <div className="bg-black/50 text-white px-2 py-1 rounded text-sm text-center">
                  {Math.round(zoomLevel * 100)}%
                </div>
              </div>
            )}

            {uploadedFiles[expandedBox] ? (
              <div className="relative overflow-hidden w-full h-full flex items-center justify-center">
                {uploadedFiles[expandedBox].type === 'video' ? (
                  <video src={uploadedFiles[expandedBox].url} className="max-w-[70vw] max-h-[80vh] object-contain" controls autoPlay />
                ) : (
                  <div
                    className="overflow-hidden w-full h-full flex items-center justify-center cursor-move"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    <img
                      src={uploadedFiles[expandedBox].url}
                      alt={`Upload ${expandedBox + 1}`}
                      className="object-contain transition-transform duration-200"
                      style={{
                        transform: `scale(${zoomLevel}) translate(${zoomPosition.x / zoomLevel}px, ${zoomPosition.y / zoomLevel}px)`,
                        maxWidth: zoomLevel === 1 ? '70vw' : 'none',
                        maxHeight: zoomLevel === 1 ? '80vh' : 'none',
                        cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                      }}
                      draggable={false}
                    />
                  </div>
                )}

                <button
                  onClick={e => {
                    e.stopPropagation();
                    handleRemoveFile(expandedBox, e);
                  }}
                  className="absolute bottom-4 right-4 bg-red-500 text-white p-3 rounded-full hover:bg-red-600 transition-all duration-300 z-20"
                >
                  <i className="ri-delete-bin-line w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>
            ) : (
              <div className="text-center p-8 bg-gray-900 rounded-lg max-w-md">
                <div className="text-white mb-4">
                  <i className="ri-upload-cloud-line text-8xl w-24 h-24 flex items-center justify-center mx-auto"></i>
                </div>
                <p className="text-white text-2xl mb-4">
                  {expandedBox === 8 ? 'Click to Upload Video' : expandedBox === 5 || expandedBox === 6 ? 'Click to Upload JPEG' : 'Click to Upload Image'}
                </p>
                <p className="text-gray-300 text-lg mb-2">
                  {expandedBox === 8
                    ? 'Supported: MP4, MOV, AVI'
                    : expandedBox === 5 || expandedBox === 6
                    ? 'Supported: JPEG, JPG only'
                    : 'Supported: JPEG, JPG, PNG'}
                </p>
                <p className="text-gray-400 text-sm mb-6">Files will be stored in Supabase Storage</p>
                <button
                  onClick={() => fileInputRefs.current[expandedBox]?.click()}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors mr-4 whitespace-nowrap cursor-pointer"
                >
                  Select File
                </button>
                <button
                  onClick={() => setExpandedBox(null)}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default LerockPage;
